import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";

// Eagerly load critical components
import Welcome from "./pages/Welcome";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/ToastProvider";
import { ConfirmProvider } from "./components/ConfirmProvider";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { warmBackend } from "./utils/backendWarmup";

// Lazy load non-critical pages for faster initial load
const Results = lazy(() => import("./pages/Results"));
const TrialDetails = lazy(() => import("./pages/TrialDetails"));
const Profile = lazy(() => import("./pages/Profile"));
const Favorites = lazy(() => import("./pages/Favorites"));
const ForYou = lazy(() => import("./pages/ForYou"));
const Pricing = lazy(() => import("./pages/Pricing"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("./Admin/AdminLogin"));
const CreateTrial = lazy(() => import("./Admin/CreateTrial"));
const AdminTrials = lazy(() => import("./Admin/AdminTrials"));
const EditTrial = lazy(() => import("./Admin/EditTrial"));
const AdminDashboard = lazy(() => import("./Admin/AdminDashboard"));
const CompanyRegister = lazy(() => import("./Admin/CompanyRegister"));
const CompanyLogin = lazy(() => import("./Admin/CompanyLogin"));
const CompanyDashboard = lazy(() => import("./Admin/CompanyDashboard"));

// Loading fallback component
const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '60vh',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid #f3f3f3',
      borderTop: '3px solid #4a9b8e',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

function PrivateRoute({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  if (user === undefined) return <div>Loading...</div>;
  return user ? children : <Navigate to="/admin-login" replace />;
}

function CompanyPrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(undefined);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("companyToken");
      const companyId = localStorage.getItem("companyId");
      
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/subscriptions/company/${companyId}`
        );
        const company = await response.json();
        
        if (company.status === 'paused') {
          setIsPaused(true);
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        setIsAuthenticated(true);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === undefined) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>;
  
  if (isPaused) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <div style={{ 
          background: 'white', 
          padding: '3rem', 
          borderRadius: '16px', 
          boxShadow: '0 2px 16px rgba(0, 0, 0, 0.08)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', marginBottom: '1rem' }}>
            Subscription Paused
          </h2>
          <p style={{ color: '#5a6c7d', fontSize: '1rem', lineHeight: '1.6', marginBottom: '2rem' }}>
            Your subscription has been paused. Please contact us to reactivate your account.
          </p>
          <a href="mailto:contact@cureiosity.org" style={{
            display: 'inline-block',
            background: '#4a9b8e',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            transition: 'background 0.2s'
          }} onMouseOver={(e) => e.target.style.background = '#3a8478'} onMouseOut={(e) => e.target.style.background = '#4a9b8e'}>
            contact@cureiosity.org
          </a>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/partner-login" replace />;
}

// 🔁 App logic including route and NavBar/Footer visibility
function AppWrapper() {
  const location = useLocation();
  const isPartnerPage =
    location.pathname === "/partners" ||
    location.pathname === "/universities" ||
    location.pathname.startsWith("/partner") ||
    location.pathname.startsWith("/university");
  const hideNav = location.pathname === "/";
  const hideFooter = location.pathname === "/";

  useEffect(() => {
    document.body.classList.toggle("nav-hidden", hideNav);
    document.body.classList.toggle("partner-nav", isPartnerPage && !hideNav);

    return () => {
      document.body.classList.remove("nav-hidden");
      document.body.classList.remove("partner-nav");
    };
  }, [hideNav, isPartnerPage]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      warmBackend();
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname]);

  return (
    <>
      {!hideNav && <NavBar partner={isPartnerPage} />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Welcome />} />
          <Route path="/home" element={<Navigate to="/FORYOU" replace />} />
          <Route path="/results" element={<Results />} />
          <Route path="/trial/:id" element={<TrialDetails />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/FORYOU" element={<ForYou />} />
          <Route path="/for-you" element={<Navigate to="/FORYOU" replace />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/explore" element={<Navigate to="/FORYOU" replace />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Admin login (public) */}
          <Route path="/admin-login" element={<AdminLogin />} />

          {/* Company routes (public) */}
          <Route path="/company-register" element={<CompanyRegister />} />
          <Route path="/company-login" element={<CompanyLogin />} />
          <Route path="/partners" element={<CompanyRegister />} />
          <Route path="/partner-register" element={<CompanyRegister />} />
          <Route path="/partner-login" element={<CompanyLogin />} />
          <Route path="/universities" element={<Navigate to="/partners" replace />} />
          <Route path="/university-register" element={<Navigate to="/partner-register" replace />} />
          <Route path="/university-login" element={<Navigate to="/partner-login" replace />} />

          {/* Company protected routes */}
          <Route
            path="/company-dashboard"
            element={
              <CompanyPrivateRoute>
                <CompanyDashboard />
              </CompanyPrivateRoute>
            }
          />
          <Route
            path="/university-dashboard"
            element={<Navigate to="/partner-dashboard" replace />}
          />
          <Route
            path="/partner-dashboard"
            element={
              <CompanyPrivateRoute>
                <CompanyDashboard />
              </CompanyPrivateRoute>
            }
          />
          <Route
            path="/university/create-trial"
            element={<Navigate to="/partner/create-trial" replace />}
          />
          <Route
            path="/partner/create-trial"
            element={
              <CompanyPrivateRoute>
                <CreateTrial />
              </CompanyPrivateRoute>
            }
          />

          {/* Admin protected routes */}
          <Route
            path="/admin-dashboard"
            element={
              <PrivateRoute>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/create-trial"
            element={
              <PrivateRoute>
                <CreateTrial />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/trials"
            element={
              <PrivateRoute>
                <AdminTrials />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/trials/edit/:id"
            element={
              <PrivateRoute>
                <EditTrial />
              </PrivateRoute>
            }
          />
        </Routes>
      </Suspense>
      {!hideFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <ConfirmProvider>
          <Router>
            <AppWrapper />
          </Router>
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
